import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faMoneyBillWave,
  faTag,
  faCalendarAlt,
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { toastError, toastSuccess } from "../../../utils/toast";

const CATEGORIES = ["Expense", "Receive"];

const AddTransaction = ({ isOpen, onClose, onSave }) => {
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    description: "",
    category: "",
    amount: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatNumberWithCommas = (value) => {
    if (!value) return "";
    const cleanValue = value.toString().replace(/[^0-9]/g, "");
    if (!cleanValue) return "";

    const num = parseFloat(cleanValue);
    if (isNaN(num)) return "";

    return num.toLocaleString("en-US");
  };

  const handleAmountChange = (e) => {
    const rawValue = e.target.value;
    const cleanValue = rawValue.replace(/,/g, "").replace(/[^0-9]/g, "");

    if (cleanValue === "") {
      setForm((prev) => ({ ...prev, amount: cleanValue }));
      if (errors.amount) {
        setErrors((prev) => ({ ...prev, amount: "" }));
      }
      return;
    }

    const num = parseFloat(cleanValue);
    if (!isNaN(num) && num >= 0) {
      setForm((prev) => ({ ...prev, amount: cleanValue }));
      if (errors.amount) {
        setErrors((prev) => ({ ...prev, amount: "" }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = () => {
    const newErrors = {};

    if (!form.date) {
      newErrors.date = "Date is required";
    }
    if (!form.description.trim()) {
      newErrors.description = "Description is required";
    }
    if (!form.category) {
      newErrors.category = "Category is required";
    }

    const amountValue = parseFloat(form.amount);
    if (form.amount === "" || isNaN(amountValue) || amountValue <= 0) {
      newErrors.amount = "Valid amount greater than 0 is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toastError("Please fix all validation errors before continuing.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = onSave({
        date: form.date,
        description: form.description,
        category: form.category.toLowerCase(),
        amount: parseFloat(form.amount),
      });

      if (result && typeof result.then === "function") {
        result
          .then(() => {
            toastSuccess(`Transaction added successfully!`);
            setIsSubmitting(false);
            setForm({
              date: new Date().toISOString().split("T")[0],
              description: "",
              category: "",
              amount: "",
            });
            onClose();
          })
          .catch((error) => {
            toastError(
              `Failed to add transaction: ${error.message || "Please try again."}`,
            );
            setIsSubmitting(false);
          });
      } else {
        toastSuccess(`Transaction added successfully!`);
        setIsSubmitting(false);
        setForm({
          date: new Date().toISOString().split("T")[0],
          description: "",
          category: "",
          amount: "",
        });
        onClose();
      }
    } catch (error) {
      toastError(
        `Failed to add transaction: ${error.message || "Please try again."}`,
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add financial record"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Add transaction
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Info Banner */}
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-blue-800 h-4 w-4"
            />
          </div>
          <div>
            <p className="text-sm text-gray-600 font-semibold">
              Add new financial records
            </p>
            <p className="text-sm text-gray-600 font-medium">
              Fill in all required fields (*)
            </p>
          </div>
        </div>

        {/* Category */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faTag}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Category <span className="text-red-700">*</span>
          </Label>
          <Select
            value={form.category}
            onValueChange={(value) => {
              setForm((prev) => ({ ...prev, category: value }));
              if (errors.category) {
                setErrors((prev) => ({ ...prev, category: "" }));
              }
            }}
          >
            <SelectTrigger
              className={`h-10 text-sm ${errors.category ? "border-red-500" : "border-gray-300"}`}
            >
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.category && (
            <p className="text-red-500 text-sm flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.category}
            </p>
          )}
        </div>

        {/* Amount */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Amount (TZS)<span className="text-red-700">*</span>
          </Label>
          <div className="relative">
            <Input
              name="amount"
              type="text"
              value={form.amount ? formatNumberWithCommas(form.amount) : ""}
              onChange={handleAmountChange}
              placeholder="Enter amount (e.g., 200,000)"
              className={`h-10 text-sm font-medium ${errors.amount ? "border-red-500" : "border-gray-300"}`}
            />
            {form.amount && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-600">
                TZS
              </div>
            )}
          </div>
          {errors.amount && (
            <p className="text-red-500 text-sm flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.amount}
            </p>
          )}
          <p className="text-xs text-orange-600 font-medium">
            Enter a positive amount greater than 0
          </p>
        </div>

        {/* Date */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Date<span className="text-red-700">*</span>
          </Label>
          <Input
            name="date"
            type="date"
            value={form.date}
            onChange={handleChange}
            className={`h-10 text-sm ${errors.date ? "border-red-500" : "border-gray-300"}`}
          />
          {errors.date && (
            <p className="text-red-500 text-sm flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.date}
            </p>
          )}
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faFileAlt}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Description
          </Label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Enter full description (e.g., Personal expenses, Salary, Borrowing from bank, Returned loan money, etc.)"
            className={`w-full h-24 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800/30 ${
              errors.description ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.description && (
            <p className="text-red-500 text-sm flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.description}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddTransaction;
